import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import config from "../../config";
import PaneelNavigatie from "../../components/Paneel/paneelNavigatie";
import { check_login, check_permission } from "../../auth/auth";
import toast from "react-hot-toast";
import useDebounce from "../../hooks/useDebounce";
import { _ } from "../../modules/functions";

type CommiUser = {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    total: number;
};

type CommiTransaction = {
    id: string;
    user: string;
    user_email: string;
    points: number;
    reason: string;
    awarded_by: string;
    awarded_by_email: string;
    created_at: string;
};

function Paneel_Commipunten() {
    const [claims] = useState(check_login(true));
    const [allowed] = useState(check_permission("praesidium", true));

    const [users, setUsers] = useState<CommiUser[]>([]);
    const [gefilterdeUsers, setGefilterdeUsers] = useState<CommiUser[]>([]);
    const [naamFilter, setNaamFilter] = useState("");
    const [selectedUser, setSelectedUser] = useState<CommiUser | null>(null);
    const [points, setPoints] = useState("");
    const [reason, setReason] = useState("");
    const [transactions, setTransactions] = useState<CommiTransaction[]>([]);

    const loadUsers = () => {
        axios
            .get(config.api_url + "/commi/users/")
            .then((response) => {
                setUsers(response.data);
                setGefilterdeUsers(response.data);
            })
            .catch(() => {
                toast.error("Commileden konden niet geladen worden.");
            });
    };

    useEffect(() => {
        if (claims && allowed) {
            loadUsers();
        }
    }, [claims, allowed]);

    useDebounce(
        () => {
            setGefilterdeUsers(users.filter((x) => _.STRING_CONTAINS(x.first_name + x.last_name + x.email, naamFilter)));
        },
        300,
        [users, naamFilter]
    );

    const addPoints = () => {
        if (!selectedUser) return;

        if (points === "" || Number(points) === 0) {
            toast.error("Het aantal punten mag niet 0 zijn.");
            return;
        }

        if (reason.trim() === "") {
            toast.error("Geef een reden op.");
            return;
        }

        axios
            .post(config.api_url + "/commi/transactions/", {
                user: selectedUser.id,
                points: Number(points),
                reason: reason
            })
            .then(() => {
                toast.success("Punten aangepast.");

                setPoints("");
                setReason("");

                loadUsers();
                loadTransactions(selectedUser.id);
            })
            .catch((error) => {
                console.error(error);
                toast.error("Punten konden niet aangepast worden.");
            });
    };

    const loadTransactions = (userId: string) => {
        axios
            .get(config.api_url + `/commi/users/${userId}/transactions/`)
            .then((response) => {
                setTransactions(response.data);
            })
            .catch(() => {
                toast.error("Puntenhistoriek kon niet geladen worden.");
            });
    };

    return (
        <>
            {claims && allowed && (
                <div className="dark:bg-zinc-900 min-h-screen">
                    <PaneelNavigatie />

                    <Helmet>
                        <title>Paneel - Commipunten - Hermes Gent</title>
                    </Helmet>

                    <div className="lg:w-8/12 w-11/12 pt-20 pb-40 mx-auto">
                        <h1 className="font-extrabold text-2xl md:text-3xl xl:text-4xl text-gray-700 dark:text-gray-300 mb-10">
                            COMMIPUNTEN
                        </h1>

                        <div className="mb-4">
                            <input
                                value={naamFilter}
                                onChange={(e) => setNaamFilter(e.target.value)}
                                type="text"
                                id="naam"
                                placeholder="Naam van gebruiker"
                                className="w-full border border-zinc-300 h-11 focus:outline-none focus:ring-1 focus:ring-blue-600 bg-transparent dark:border-zinc-700 px-3 dark:text-zinc-400 rounded-md"
                            />
                        </div>

                        {gefilterdeUsers.length === 0 && <div className="text-zinc-400 dark:text-zinc-600">Geen gebruikers die aan deze voorwaarden voldoen.</div>}

                        <div className="h-96 overflow-y-scroll overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead className="sticky top-0 bg-white dark:bg-zinc-900">
                                    <tr className="border-b border-zinc-300 dark:border-zinc-700">
                                        <th className="text-left p-4 text-gray-700 dark:text-gray-300">
                                            Naam
                                        </th>
                                        <th className="text-left p-4 text-gray-700 dark:text-gray-300">
                                            E-mail
                                        </th>
                                        <th className="text-left p-4 text-gray-700 dark:text-gray-300">
                                            Punten
                                        </th>
                                        <th className="p-4"></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {gefilterdeUsers.map((user) => (
                                        <tr
                                            key={user.id}
                                            className="border-b border-zinc-200 dark:border-zinc-800"
                                        >
                                            <td className="p-4 text-zinc-800 dark:text-zinc-200">
                                                {user.first_name} {user.last_name}
                                            </td>

                                            <td className="p-4 text-zinc-800 dark:text-zinc-200">
                                                {user.email}
                                            </td>

                                            <td className="p-4 font-bold text-zinc-800 dark:text-zinc-200">
                                                {user.total}
                                            </td>

                                            <td className="p-4 text-right">
                                                <button
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        loadTransactions(user.id);
                                                    }}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
                                                >
                                                    Punten aanpassen
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {selectedUser && (
                            <div className="mt-10 border border-zinc-200 dark:border-zinc-800 rounded-lg p-6 shadow-md dark:bg-black/40">
                                <h2 className="font-bold text-xl text-gray-700 dark:text-gray-300 mb-6">
                                    Punten aanpassen voor {selectedUser.first_name} {selectedUser.last_name}
                                </h2>

                                <div className="mb-4">
                                    <label className="block mb-2 text-zinc-700 dark:text-zinc-300">
                                        Punten
                                    </label>

                                    <input
                                        type="number"
                                        value={points}
                                        onChange={(e) => setPoints(e.target.value)}
                                        className="w-full border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white rounded p-3"
                                    />

                                    <p className="text-sm text-zinc-500 mt-2">
                                        Gebruik een negatief getal om punten af te trekken.
                                    </p>
                                </div>

                                <div className="mb-6">
                                    <label className="block mb-2 text-zinc-700 dark:text-zinc-300">
                                        Reden
                                    </label>

                                    <input
                                        type="text"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        className="w-full border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white rounded p-3"
                                        placeholder="Bijvoorbeeld: geholpen op kringavond"
                                    />
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={addPoints}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded transition-colors"
                                    >
                                        Opslaan
                                    </button>

                                    <button
                                        onClick={() => {
                                            setSelectedUser(null);
                                            setTransactions([]);
                                            setPoints("");
                                            setReason("");
                                        }}
                                        className="border border-zinc-300 dark:border-zinc-700 px-5 py-2 rounded text-zinc-700 dark:text-zinc-300"
                                    >
                                        Annuleren
                                    </button>
                                </div>
                                <div className="mt-10">
                                    <h3 className="font-bold text-lg text-gray-700 dark:text-gray-300 mb-4">
                                        Historiek
                                    </h3>

                                    {transactions.length === 0 ? (
                                        <p className="text-zinc-500">
                                            Nog geen puntentransacties.
                                        </p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="border-b border-zinc-300 dark:border-zinc-700">
                                                        <th className="text-left p-3 text-gray-700 dark:text-gray-300">
                                                            Punten
                                                        </th>
                                                        <th className="text-left p-3 text-gray-700 dark:text-gray-300">
                                                            Reden
                                                        </th>
                                                        <th className="text-left p-3 text-gray-700 dark:text-gray-300">
                                                            Door
                                                        </th>
                                                        <th className="text-left p-3 text-gray-700 dark:text-gray-300">
                                                            Datum
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {transactions.map((transaction) => (
                                                        <tr
                                                            key={transaction.id}
                                                            className="border-b border-zinc-200 dark:border-zinc-800"
                                                        >
                                                            <td
                                                                className={`p-3 font-bold ${transaction.points > 0
                                                                    ? "text-green-600"
                                                                    : "text-red-600"
                                                                    }`}
                                                            >
                                                                {transaction.points > 0 ? "+" : ""}
                                                                {transaction.points}
                                                            </td>

                                                            <td className="p-3 text-zinc-800 dark:text-zinc-200">
                                                                {transaction.reason}
                                                            </td>

                                                            <td className="p-3 text-zinc-800 dark:text-zinc-200">
                                                                {transaction.awarded_by_email}
                                                            </td>

                                                            <td className="p-3 text-zinc-800 dark:text-zinc-200">
                                                                {new Date(transaction.created_at).toLocaleString("nl-BE")}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default Paneel_Commipunten;